import { isEmpty } from 'lodash';
import React, { useCallback } from 'react';
import { useLocation } from 'react-router';

import HistoryIcon from '@mui/icons-material/History';
import { Button, Chip, CircularProgress, Menu, MenuItem, SvgIcon } from '@mui/material';

import API from 'app/API';
import useLoginDialog from 'hooks/use-login-dialog';
import useStores from 'hooks/use-stores';
import { useConfigs } from 'modules/configs';
import VersionMenuItem from 'pages/Bulletin/components/RevisionSelect/components/VersionMenuItem';
import { useHistoryOfChanges } from 'pages/Bulletin/providers/history-of-changes';
import ProtectedData, { LockIcon } from 'pages/components/ProtectedData';
import formatDate from 'utils/format-date';

import DownloadIcon from './assets/download.svg';
import useStyles from './styles';

interface Props {
    id: string;
    published?: string;
    className?: string;
}

const RevisionSelect: React.FC<Props> = ({ id, published, className }) => {
    const { classes, cx } = useStyles();
    const { userStore } = useStores();
    const { settings } = useConfigs();
    const location = useLocation();
    const [dialog, openDialog] = useLoginDialog(location.pathname + location.search);

    const { data, revision, setVersion, isLoading, load } = useHistoryOfChanges();
    const date = revision?.published ?? published ?? null;

    const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
    const handleOpen = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            load();
            setAnchor(event.currentTarget);
        },
        [load]
    );
    const handleClose = useCallback(() => setAnchor(null), []);

    const handleSelect = useCallback(
        (version: number) => {
            setVersion(version);
            handleClose();
        },
        [handleClose, setVersion]
    );

    if (!userStore.isAuthorized && settings.limitDataEnabled) {
        return (
            <>
                <Chip
                    {...{ disableRipple: true }}
                    className={cx(classes.chip, classes.disabled, className)}
                    icon={<LockIcon className={classes.icon} />}
                    variant="outlined"
                    clickable
                    onClick={openDialog}
                    label={
                        <div className={classes.label}>
                            <span className={classes.text}>{'History'}</span>
                        </div>
                    }
                />
                {dialog}
            </>
        );
    }

    return (
        <ProtectedData data={date}>
            {({ isProtected, content }) => (
                <>
                    <Chip
                        {...{ disableRipple: true }}
                        className={cx(classes.chip, className)}
                        icon={<HistoryIcon className={classes.icon} />}
                        variant="outlined"
                        clickable
                        onClick={isProtected ? undefined : handleOpen}
                        label={
                            <div className={classes.label}>
                                {isProtected ? null : (
                                    <span className={classes.date} suppressHydrationWarning>
                                        {formatDate(content, 'dd LLL yyyy HH:mm')}
                                    </span>
                                )}
                                <span className={classes.text}>
                                    {revision?.version
                                        ? revision.version === data[0]?.version
                                            ? 'Current'
                                            : `ver. ${revision.version}`
                                        : 'Current'}
                                </span>
                            </div>
                        }
                    />
                    <Menu
                        classes={{ paper: classes.menu, list: classes.menuList }}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right'
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right'
                        }}
                        anchorEl={anchor}
                        open={!!anchor && !isProtected}
                        onClose={handleClose}
                    >
                        <div className={classes.menuTitle}>{'🗓 history of updates'}</div>
                        {isLoading ? (
                            <div className={classes.spinner}>
                                <CircularProgress size={24} />
                            </div>
                        ) : isEmpty(data) ? (
                            <MenuItem disabled className={classes.empty}>
                                {'Nothing found'}
                            </MenuItem>
                        ) : (
                            data.map((item) => (
                                <VersionMenuItem
                                    key={item.version}
                                    item={item}
                                    onSelect={handleSelect}
                                    selected={item.version === revision?.version}
                                    current={item.version === data[0]?.version}
                                />
                            ))
                        )}
                        <div key={'download-history'} className={classes.download}>
                            <Button
                                className={classes.button}
                                variant="contained"
                                href={API.SEARCH_HISTORY.url.replace('{id}', id)}
                                target="__blank"
                                startIcon={
                                    <SvgIcon inheritViewBox>
                                        <DownloadIcon />
                                    </SvgIcon>
                                }
                            >
                                {'Download history .json'}
                            </Button>
                        </div>
                    </Menu>
                </>
            )}
        </ProtectedData>
    );
};

export default RevisionSelect;
